import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { captureUTM } from "./utils/analytics";

// Pages
import HomePage from "./pages/HomePage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";
import EEATAuditDashboard from "./pages/EEATAuditDashboard";
import ConversionAuditDashboard from "./pages/ConversionAuditDashboard";
import LocalSEOPage from "./pages/LocalSEOPage";
import AcharyaDashboard from "./pages/AcharyaDashboard";
import ClientKundliPage from "./pages/ClientKundliPage";
import GunMilanPage from "./pages/GunMilanPage";
import DailyRashifalPage from "./pages/DailyRashifalPage";
import DailyPanchangPage from "./pages/DailyPanchangPage";
import DailyTarotPage from "./pages/DailyTarotPage";
import NumerologyPage from "./pages/NumerologyPage";
import KundliReport from "./pages/KundliReport";
import KundliPage from "./pages/KundliPage";
import FAQPage from "./pages/FAQPage";
import AboutPage from "./pages/AboutPage";
import PoochoGurujiPage from "./pages/PoochoGurujiPage";
import BookingPage from "./pages/BookingPage";
import GrowthOSDashboard from "./pages/GrowthOSDashboard";
import UpayPage from "./pages/UpayPage";
import UpayPlanetPage from "./pages/UpayPlanetPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import PriyaKhuranaPage from "./pages/PriyaKhuranaPage";
import VivekMudgalPage from "./pages/VivekMudgalPage";
import ExpertsDirectoryPage from "./pages/ExpertsDirectoryPage";
import ConversionsAdminPage from "./pages/ConversionsAdminPage";
import PoochoRecoverAdminPage from "./pages/PoochoRecoverAdminPage";
import PoochoAnswerViewPage from "./pages/PoochoAnswerViewPage";
import BrandPage from "./pages/BrandPage";
import AutoBlogEnginePage from "./pages/AutoBlogEnginePage";
import IntegrationCenterPage from "./pages/IntegrationCenterPage";
import RoadmapPage from "./pages/RoadmapPage";
import NotFoundPage from "./pages/NotFoundPage";
import StickyMobileCTA from "./components/StickyMobileCTA";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

// Public Route (redirect if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      {/* Feb 2026: Root now redirects to /poocho-guruji (primary paid product landing).
          Original HomePage still accessible at /home for legacy inbound links. */}
      <Route path="/" element={<Navigate to="/poocho-guruji" replace />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogDetailPage />} />
      <Route path="/gun-milan" element={<GunMilanPage />} />
      <Route path="/daily-rashifal" element={<DailyRashifalPage />} />
      <Route path="/daily-panchang" element={<DailyPanchangPage />} />
      <Route path="/daily-tarot" element={<DailyTarotPage />} />
      <Route path="/numerology" element={<NumerologyPage />} />
      <Route path="/kundli/report" element={<KundliReport />} />
      <Route path="/kundli" element={<KundliPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/faq/:faqSlug" element={<FAQPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/poocho-guruji" element={<PoochoGurujiPage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route
        path="/admin/growth-os"
        element={
          <ProtectedRoute>
            <GrowthOSDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/experts" element={<ExpertsDirectoryPage />} />
      <Route path="/admin/conversions" element={<ConversionsAdminPage />} />
      <Route path="/poocho-guruji/answer/:sessionId" element={<PoochoAnswerViewPage />} />
      <Route
        path="/admin/poocho-recover"
        element={
          <ProtectedRoute>
            <PoochoRecoverAdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/acharya-dashboard" element={<AcharyaDashboard />} />
      <Route path="/acharya-dashboard/client/:id" element={<ClientKundliPage />} />
      <Route path="/experts/priya-khurana" element={<PriyaKhuranaPage />} />
      <Route path="/tarot-reader-dubai-priya-khurana" element={<Navigate to="/experts/priya-khurana" replace />} />
      <Route path="/experts/vivek-virat-mudgal" element={<VivekMudgalPage />} />
      <Route path="/experts/vivek-mudgal" element={<Navigate to="/experts/vivek-virat-mudgal" replace />} />
      <Route path="/author/vivek-virat-mudgal" element={<Navigate to="/experts/vivek-virat-mudgal" replace />} />
      <Route path="/upay" element={<UpayPage />} />
      <Route path="/upay/:planetId" element={<UpayPlanetPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/refund" element={<Navigate to="/refund-policy" replace />} />
      <Route path="/cancellation-policy" element={<Navigate to="/refund-policy" replace />} />
      <Route path="/brand" element={<BrandPage />} />
      
      {/* Auth Routes */}
      <Route 
        path="/admin/login" 
        element={
          <PublicRoute>
            <AdminLoginPage />
          </PublicRoute>
        } 
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Admin Routes */}
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/auto-blog-engine"
        element={
          <ProtectedRoute>
            <AutoBlogEnginePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute>
            <IntegrationCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roadmap"
        element={
          <ProtectedRoute>
            <RoadmapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eeat-audit"
        element={
          <ProtectedRoute>
            <EEATAuditDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/conversion-audit"
        element={
          <ProtectedRoute>
            <ConversionAuditDashboard />
          </ProtectedRoute>
        }
      />
      {/* Feb 08, 2026 P2 — Local SEO landing pages */}
      <Route path="/local/:citySlug" element={<LocalSEOPage />} />
      
      {/* Redirect /admin to /admin/dashboard */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      
      {/* 404 Fallback */}
      {/* Feb 12, 2026 SEO Fix — Replaced `Navigate to="/"` with proper 404 page.
          Previously ALL unknown URLs redirected to homepage → Google flagged
          as "Soft 404" (audit finding #5). NotFoundPage emits noindex meta. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // Capture UTM / gclid / fbclid on first landing — used for lead attribution
  useEffect(() => { captureUTM(); }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <StickyMobileCTA />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
